import React from "react";
import CssBaseline from "@mui/material/CssBaseline";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import {
  WEBPROFIT_MOBILE_TYPOGRAPHY,
  webProfitResponsiveComponents,
} from "./webProfitResponsive";

const PANEL_THEME_STORAGE_KEY = "webprofit:panel-theme";
const PANEL_THEME_MODES = new Set(["light", "dark"]);

const WebProfitThemeModeContext = React.createContext({
  mode: "light",
  preference: "light",
  isPanelRoute: false,
  setMode: () => {},
  toggleMode: () => {},
});

export function isWebProfitPanelPath(pathname = "") {
  return (
    pathname.startsWith("/dashboard") ||
    /^\/(?:owner|vendedores|superadmin)(?:\/|$)/.test(pathname) ||
    pathname === "/login" ||
    pathname === "/registro" ||
    pathname === "/confirmar-email" ||
    pathname === "/olvide-contrasena" ||
    pathname === "/restablecer-contrasena" ||
    pathname.startsWith("/editor-web")
  );
}

export function isWebProfitLandingEditorPath(pathname = "") {
  return pathname.startsWith("/editor-web");
}

function readStoredPanelTheme() {
  if (typeof window === "undefined") return "light";
  try {
    const value = window.localStorage.getItem(PANEL_THEME_STORAGE_KEY);
    return PANEL_THEME_MODES.has(value) ? value : "light";
  } catch (_error) {
    return "light";
  }
}

function persistPanelTheme(mode) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PANEL_THEME_STORAGE_KEY, mode);
  } catch (_error) {
    // El tema sigue funcionando aunque el navegador bloquee localStorage.
  }
}

function dashboardCssVariables(dark) {
  return {
    "--wp-panel-bg": dark ? "#17181C" : "#F6F7FB",
    "--wp-panel-surface": dark ? "#1E2025" : "#FFFFFF",
    "--wp-panel-surface-elevated": dark ? "#26282E" : "#FFFFFF",
    "--wp-panel-surface-soft": dark ? "#23252A" : "#F7F8FB",
    "--wp-panel-surface-rgb": dark ? "30,32,37" : "255,255,255",
    "--wp-panel-text-primary": dark ? "#F7F8FA" : "#19242F",
    "--wp-panel-text-secondary": dark ? "#C8CCD3" : "rgba(25,36,47,.62)",
    "--wp-panel-ink-rgb": dark ? "247,248,250" : "25,36,47",
    "--wp-panel-border": dark ? "rgba(247,248,250,.13)" : "rgba(25,36,47,.09)",
    "--wp-panel-shadow": dark ? "rgba(0,0,0,.46)" : "rgba(25,36,47,.16)",
    "--wp-panel-shadow-rgb": dark ? "243,90,58" : "25,36,47",
    "--wp-panel-orange": "#F35A3A",
    "--wp-panel-orange-soft": dark ? "rgba(243,90,58,.12)" : "rgba(243,90,58,.08)",
    "--wp-status-info-bg": dark ? "#172432" : "#EEF5FF",
    "--wp-status-info-border": dark ? "#315D88" : "#B9D5FF",
    "--wp-status-info-icon": dark ? "#7AB8F0" : "#2463A8",
    "--wp-status-info-text": dark ? "#D7E9FF" : "#173B65",
    "--wp-status-success-bg": dark ? "#17271E" : "#ECF9F1",
    "--wp-status-success-border": dark ? "#347250" : "#A9DEC0",
    "--wp-status-success-icon": dark ? "#74D49D" : "#1E7A46",
    "--wp-status-success-text": dark ? "#D8F5E4" : "#175A36",
    "--wp-status-warning-bg": dark ? "#302615" : "#FFF6E8",
    "--wp-status-warning-border": dark ? "#8B682C" : "#F4CF91",
    "--wp-status-warning-icon": dark ? "#F4B95D" : "#A86200",
    "--wp-status-warning-text": dark ? "#FFE4B6" : "#744600",
    "--wp-status-danger-bg": dark ? "#321A1C" : "#FFF0F0",
    "--wp-status-danger-border": dark ? "#8C4147" : "#F2B8B8",
    "--wp-status-danger-icon": dark ? "#F08A91" : "#B32D2D",
    "--wp-status-danger-text": dark ? "#FFDADD" : "#7C2020",
    "--wp-status-neutral-bg": dark ? "#292B31" : "#F2F3F5",
  };
}

function mergeComponentSlot(componentName, slotName, extraStyles) {
  const baseComponent = webProfitResponsiveComponents[componentName] || {};
  const baseOverrides = baseComponent.styleOverrides || {};
  const baseSlot = baseOverrides[slotName];

  return {
    ...baseComponent,
    styleOverrides: {
      ...baseOverrides,
      [slotName]: (params) => ({
        ...(typeof baseSlot === "function" ? baseSlot(params) : (baseSlot || {})),
        ...(typeof extraStyles === "function" ? extraStyles(params) : (extraStyles || {})),
      }),
    },
  };
}

export function createWebProfitTheme(mode = "light") {
  const dark = mode === "dark";
  const palette = {
    mode,
    primary: {
      main: "#F35A3A",
      dark: "#D9472B",
      light: "#FF8066",
      contrastText: dark ? "#17181C" : "#FFFFFF",
    },
    secondary: { main: "#27AE60" },
    terciary: { main: dark ? "#F7F8FA" : "#2C3E50" },
    success: { main: dark ? "#58C986" : "#018E42" },
    warning: { main: dark ? "#F0B45A" : "#ED6C02" },
    info: { main: dark ? "#72AFE7" : "#2463A8" },
    error: { main: dark ? "#E9787F" : "#D32F2F" },
    white: { main: "#fff" },
    background: {
      default: dark ? "#17181C" : "#FFFFFF",
      paper: dark ? "#1E2025" : "#FFFFFF",
    },
    text: {
      primary: dark ? "#F7F8FA" : "#19242F",
      secondary: dark ? "#C8CCD3" : "rgba(25,36,47,.62)",
      disabled: dark ? "rgba(247,248,250,.58)" : "rgba(25,36,47,.42)",
    },
    divider: dark ? "rgba(247,248,250,.13)" : "rgba(25,36,47,.10)",
    ...(dark
      ? {
          action: {
            active: "rgba(247,248,250,.82)",
            hover: "rgba(247,248,250,.065)",
            selected: "rgba(243,90,58,.12)",
            disabled: "rgba(247,248,250,.48)",
            disabledBackground: "rgba(247,248,250,.08)",
            focus: "rgba(243,90,58,.16)",
          },
        }
      : {}),
  };

  return createTheme({
    palette,
    typography: {
      fontFamily: ["Montserrat", "sans-serif"].join(","),
      ...WEBPROFIT_MOBILE_TYPOGRAPHY,
    },
    breakpoints: {
      values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
    },
    components: {
      ...webProfitResponsiveComponents,
      MuiCssBaseline: {
        styleOverrides: {
          ":root": dashboardCssVariables(dark),
          body: {
            color: palette.text.primary,
            backgroundColor: palette.background.default,
            transition: "background-color 160ms ease, color 160ms ease",
          },
          "#root": {
            backgroundColor: palette.background.default,
            transition: "background-color 160ms ease",
          },
          "*": {
            scrollbarColor: dark ? "#50535C #17181C" : "#C9CED6 #F6F7FB",
          },
          "*::selection": {
            backgroundColor: dark ? "rgba(243,90,58,.34)" : "rgba(243,90,58,.22)",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            ...(dark ? { borderColor: "rgba(247,248,250,.13)" } : {}),
          },
        },
      },
      MuiCard: mergeComponentSlot("MuiCard", "root", {
        backgroundImage: "none",
        ...(dark
          ? {
              borderColor: "rgba(247,248,250,.13)",
              boxShadow: "0 14px 38px rgba(243,90,58,.055)",
            }
          : {}),
      }),
      MuiDialog: mergeComponentSlot("MuiDialog", "paper", {
        backgroundImage: "none",
        ...(dark
          ? {
              border: "1px solid rgba(247,248,250,.12)",
              boxShadow: "0 28px 80px rgba(243,90,58,.12)",
            }
          : {}),
      }),
      MuiDrawer: mergeComponentSlot("MuiDrawer", "paper", {
        backgroundImage: "none",
        ...(dark
          ? {
              borderColor: "rgba(247,248,250,.13)",
              boxShadow: "12px 0 36px rgba(243,90,58,.06)",
            }
          : {}),
      }),
      MuiPopover: mergeComponentSlot("MuiPopover", "paper", {
        backgroundImage: "none",
        ...(dark
          ? {
              border: "1px solid rgba(247,248,250,.12)",
              boxShadow: "0 20px 58px rgba(243,90,58,.10)",
            }
          : {}),
      }),
      MuiMenu: mergeComponentSlot("MuiMenu", "paper", {
        backgroundImage: "none",
      }),
      MuiButton: mergeComponentSlot("MuiButton", "root", {
        ...(dark
          ? {
              "&.MuiButton-containedPrimary": {
                boxShadow: "0 9px 24px rgba(243,90,58,.26)",
                "&:hover": { boxShadow: "0 11px 30px rgba(243,90,58,.34)" },
              },
              "&.MuiButton-outlined": {
                borderColor: "rgba(247,248,250,.20)",
              },
            }
          : {}),
      }),
      MuiIconButton: mergeComponentSlot("MuiIconButton", "root", {
        ...(dark
          ? {
              color: palette.text.primary,
              "&:hover": { backgroundColor: "rgba(243,90,58,.10)" },
            }
          : {}),
      }),
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            ...(dark ? { backgroundColor: "rgba(247,248,250,.045)" } : {}),
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: dark ? "rgba(247,248,250,.20)" : "rgba(25,36,47,.18)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: dark ? "rgba(247,248,250,.34)" : "rgba(25,36,47,.30)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#F35A3A",
            },
          },
          input: {
            ...(dark ? { color: palette.text.primary } : {}),
            "&::placeholder": {
              color: dark ? "rgba(247,248,250,.64)" : "rgba(25,36,47,.50)",
              opacity: 1,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { color: palette.text.secondary },
        },
      },
      MuiFormHelperText: mergeComponentSlot("MuiFormHelperText", "root", {
        ...(dark ? { color: "rgba(247,248,250,.72)" } : {}),
      }),
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: palette.divider,
            ...(dark ? { color: palette.text.primary } : {}),
          },
          head: {
            ...(dark
              ? {
                  backgroundColor: "#23252A",
                  color: "#F7F8FA",
                }
              : {}),
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: palette.divider },
        },
      },
      MuiTabs: mergeComponentSlot("MuiTabs", "root", {
        ...(dark ? { borderColor: "rgba(247,248,250,.13)" } : {}),
      }),
      MuiTab: {
        styleOverrides: {
          root: {
            ...(dark ? { color: "rgba(247,248,250,.72)" } : {}),
            "&.Mui-selected": { color: dark ? "#FFFFFF" : undefined },
          },
        },
      },
      MuiChip: mergeComponentSlot("MuiChip", "root", {
        ...(dark
          ? {
              color: palette.text.primary,
              "&.MuiChip-outlined": { borderColor: "rgba(247,248,250,.22)" },
            }
          : {}),
      }),
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: dark ? "#2A2C32" : "#19242F",
            color: "#FFFFFF",
            ...(dark ? { border: "1px solid rgba(243,90,58,.28)" } : {}),
          },
          arrow: {
            ...(dark ? { color: "#2A2C32" } : {}),
          },
        },
      },
    },
  });
}

export function WebProfitThemeProvider({ children }) {
  const location = useLocation();
  const [preference, setPreference] = React.useState(readStoredPanelTheme);
  const isPanelRoute = isWebProfitPanelPath(location.pathname);
  const isLandingEditorRoute = isWebProfitLandingEditorPath(location.pathname);
  // # PUBLIC ENTRY: login and signup stay light. Preserve the preference so
  // entering the existing dashboard still restores the user's chosen theme.
  const themeEnabledForRoute = isPanelRoute && !/^\/owner(?:\/|$)/.test(location.pathname) && !isLandingEditorRoute && !["/registro", "/login"].includes(location.pathname);
  const mode = themeEnabledForRoute ? preference : "light";
  const theme = React.useMemo(() => createWebProfitTheme(mode), [mode]);

  const setMode = React.useCallback((nextMode) => {
    const normalized = PANEL_THEME_MODES.has(nextMode) ? nextMode : "light";
    setPreference(normalized);
    persistPanelTheme(normalized);
  }, []);

  const toggleMode = React.useCallback(() => {
    setPreference((current) => {
      const next = current === "dark" ? "light" : "dark";
      persistPanelTheme(next);
      return next;
    });
  }, []);

  React.useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const html = document.documentElement;
    if (themeEnabledForRoute) {
      html.dataset.webprofitPanelTheme = mode;
      html.style.colorScheme = mode;
    } else {
      delete html.dataset.webprofitPanelTheme;
      html.style.colorScheme = "light";
    }
    return () => {};
  }, [themeEnabledForRoute, mode]);

  const value = React.useMemo(
    () => ({ mode, preference, isPanelRoute, setMode, toggleMode }),
    [mode, preference, isPanelRoute, setMode, toggleMode],
  );

  return (
    <WebProfitThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </WebProfitThemeModeContext.Provider>
  );
}

export function useWebProfitThemeMode() {
  return React.useContext(WebProfitThemeModeContext);
}
